/**
 * An interface for sending events to GA4.
 *
 * Note that all events must be added to the GTM console.
 * Sending an event with an unknown name produces no results,
 * no requests, hard to debug.
 *
 * Event names are defined here:
 * https://tagmanager.google.com/#/container/accounts/6000141753/containers/30150878/workspaces/20/tags
 *
 * Parameter names are also important, defined here:
 * https://tagmanager.google.com/#/container/accounts/6000141753/containers/30150878/workspaces/20/variables
 */

import { apiMain } from "services";
import { isAuthenticated } from "services/Auth";
import { loadExperiments } from "utils/storage";

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer: any[];
  }
}

export enum ViewRegion {
  Collections = "collections",
  Embed = "embed",
  Explore = "explore",
  Favourites = "favourites",
  ForYou = "foryou",
  Main = "main",
  Niche = "niche",
  Other = "other",
  Profile = "profile",
  Related = "related",
  Search = "search",
}

class Metrics {
  async sendAddedToCollection(props: {
    gif: string;
  }): Promise<void> {
    await this.sendRawEvent({
      event: "addedToCollection",
      gifId: props.gif,
    });
  }

  /**
   * Send when the ad script loaded successfully.
   * Not for tracking individual ads.
   */
  async sendAdLoaded(value: boolean): Promise<void> {
    await this.sendRawEvent({
      event: "AdLoaded",
      isAdLoaded: value,
    });
  }

  async sendEncodingFinished(): Promise<void> {
    await this.sendRawEvent({
      event: "encodingFinished",
    });
  }

  async sendEncodingStarted(props: {
    sourceURL?: string;
  }): Promise<void> {
    await this.sendRawEvent({
      event: "encodingStarted",
      sourceUrl: props.sourceURL,
    });
  }

  async sendFollowUserClicked(props: {
    userName: string;
  }): Promise<void> {
    await this.sendRawEvent({
      event: "followClicked",
      username_clicked: props.userName,
      username_owner: this.getUserName(),
    });
  }

  async sendGifClick(props: {
    // Gif position in the list, 1 to infinity.
    position: number;
    // Where the gif is displayed.  Add more if you need.
    source: ViewRegion;
  }): Promise<void> {
    await this.sendRawEvent({
      event: "gifClick",
      source: props.source,
      position: props.position,
    });
  }

  async sendGifView(props: {
    gifId: string;
    tags: string[];
    fullscreen: boolean;
    source: ViewRegion;
    position: number;
    boosted: boolean;
    userName?: string | null;
  }): Promise<void> {
    await this.sendRawEvent({
      event: "gifView",
      gifId: props.gifId,
      tags: props.tags,
      fullscreen: props.fullscreen,
      source: props.source,
    });

    await this.sendPixelGifView({
      gifId: props.gifId,
      position: props.position,
      source: props.source,
      boosted: props.boosted,
      userName: props.userName,
    });
  }

  async sendHeaderClick(props: {
    id: string;
  }): Promise<void> {
    await this.sendRawEvent({
      event: "headerClick",
      header_id: props.id,
    });
  }

  async sendLiked(props: {
    gif: string;
  }): Promise<void> {
    await this.sendRawEvent({
      event: "liked",
      gifId: props.gif,
    });
  }

  async sendMenuButtonClicked(props: {
    name: string;
    pageType?: string;
    category?: string;
  }): Promise<void> {
    await this.sendRawEvent({
      event: "menuButtonClicked",
      buttonName: props.name,
      pageType: props.pageType,
      category: props.category,
    });
  }

  async sendProfileView(props: {
    userName: string;
  }): Promise<void> {
    await this.sendRawEvent({
      event: "profileView",
      username_clicked: this.getUserName(),
      username_owner: props.userName,
    });
  }

  async sendSearch(props: {
    query: string;
    totalCount: number;
    pageType?: string;
    category?: string;
  }): Promise<void> {
    await this.sendRawEvent({
      event: "search",
      query: props.query,
      count: props.totalCount,
      pageType: props.pageType,
      category: props.category,
    });
  }

  async sendSignUpFormView(): Promise<void> {
    await this.sendRawEvent({
      event: "signUpView",
    });
  }

  async sendSignUpSuccessful(): Promise<void> {
    await this.sendRawEvent({
      event: "signUpSuccess",
    });
  }

  async sendSocialProfileClick(props: {
    userName: string; // profile owner
    type: string; // link type, e.g. "socialUrl1"
  }): Promise<void> {
    await this.sendRawEvent({
      event: "socialProfileClicked",
      username_clicked: this.getUserName(),
      username_owner: props.userName,
    });
  }

  async sendTagClicked(props: {
    tag: string;
    pageType?: string;
    category?: string;
  }): Promise<void> {
    await this.sendRawEvent({
      event: "tagClicked",
      tagName: props.tag,
      pageType: props.pageType,
      category: props.category,
    });
  }

  async sendUploadFinisher(props: {
    sourceURL?: string;
  }): Promise<void> {
    await this.sendRawEvent({
      event: "uploadFinished",
      source: props.sourceURL,
    });
  }

  async sendUploadStarted(props: {
    context: "create";
  }): Promise<void> {
    await this.sendRawEvent({
      event: "uploadStarted",
      context: props.context,
    });
  }

  async sendTagsAdded(props: {
    tags: string[];
  }): Promise<void> {
    await this.sendRawEvent({
      event: "3tagsAdded",
      tags: props.tags,
    });
  }

  async sendUploadUrlSelected(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadUrlSelected"
    });
  }

  async sendUploadImageSelected(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadImageSelected"
    });
  }

  async sendUploadImageConfirmed(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadImageConfirmed"
    });
  }

  async sendUploadVideoSelected(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadVideoSelected"
    });
  }

  async sendUploadVideoTrim(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadVideoTrim"
    });
  }

  async sendUploadVideoConfirmed(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadVideoConfirmed"
    });
  }

  async sendUploadAudienceDefined(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadAudienceDefined"
    });
  }

  async sendUploadTagsSelected(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadTagsSelected"
    });
  }

  async sendUploadGetVerifiedClicked(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadGetVerifiedClicked"
    });
  }

  async sendUploadPublished(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadPublished"
    });
  }

  async sendUploadMultipleConfirmed(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadMultipleConfirmed"
    });
  }

  async sendUploadVideoDropped(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadVideoDropped"
    });
  }

  async sendUploadImageDropped(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadImageDropped"
    });
  }

  async sendUploadImagesDropped(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadImagesDropped"
    });
  }

  async sendOptionClick(): Promise<void> {
    await this.sendRawEvent({
      event: "optionClick"
    });
  }

  async sendShareClick(): Promise<void> {
    await this.sendRawEvent({
      event: "shareClick"
    });
  }

  async sendShareCopy(): Promise<void> {
    await this.sendRawEvent({
      event: "shareCopy"
    });
  }

  async sendShareEmbed(): Promise<void> {
    await this.sendRawEvent({
      event: "shareEmbed"
    });
  }

  async sendShareReddit(): Promise<void> {
    await this.sendRawEvent({
      event: "shareReddit"
    });
  }

  async sendShareTwitter(): Promise<void> {
    await this.sendRawEvent({
      event: "shareTwitter"
    });
  }

  async sendShareDiscord(): Promise<void> {
    await this.sendRawEvent({
      event: "shareDiscord"
    });
  }

  async sendShareEmbedFixed(): Promise<void> {
    await this.sendRawEvent({
      event: "shareEmbedFixed"
    });
  }

  async sendShareEmbedResponsive(): Promise<void> {
    await this.sendRawEvent({
      event: "shareEmbedResponsive"
    });
  }

  async sendEnterFullscreen(): Promise<void> {
    await this.sendRawEvent({
      event: "enterFullscreen"
    });
  }

  async sendScrollPromptShow(): Promise<void> {
    await this.sendRawEvent({
      event: "scrollPromptShow"
    });
  }

  async sendScrollPromptDismiss(): Promise<void> {
    await this.sendRawEvent({
      event: "scrollPromptDismiss"
    });
  }

  async sendUploadBulkClick(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadBulkClick"
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async sendRawEvent(event: any) {
    window.dataLayer = window.dataLayer ? window.dataLayer : [];

    event.userLogIn = await this.isUserLoggedIn();
    event.userName = await this.getUserName();
    event.experiments = await this.getExperiments();
    event.userVerified = await this.getUserVerifiedStatus();

    const res = window.dataLayer.push(event);

    // @ts-ignore
    if (res === true) {
      console.warn(`[GTM] Event not set, make sure that "${event.event}" is added properly`
        + " in the console here: https://tagmanager.google.com/#/container/accounts/6000141753/containers/30150878/workspaces/20/tags", event);
    } else {
      console.debug("[GTM] Event sent.", event);
    }
  }

  async isUserLoggedIn(): Promise<boolean> {
    try {
      return await isAuthenticated();
    } catch (e) {
      console.warn("[GTM] Could not tell if the user is authenticated, assuming no.");
      return false;
    }
  }

  async getExperiments() {
    const storageExperiments = loadExperiments();

    if (storageExperiments) {
      return Object.keys(storageExperiments).filter((key) => storageExperiments[key] === true);
    }

    const apiExperiments = await apiMain.getExperiments();

    if (!apiExperiments) {
      return [];
    }

    return Object.keys(apiExperiments).filter((key: string) => apiExperiments[key] === true);
  }

  async sendPixelGifView(props: {
    gifId: string;
    position: number;
    source: ViewRegion;
    boosted: boolean;
    userName?: string | null;
  }): Promise<void> {
    try {
      await apiMain.sendGifMetrics({
        event: "view",
        gif: props.gifId,
        position: props.position,
        source: props.source,
        boosted: props.boosted,
        username: props.userName
      });
    } catch (err) {
      console.error("Error sending metrics.");
    }
  }

  async sendGifPlayback(props: {
    duration: number;
    gif: string;
    context: string | null;
  }): Promise<void> {
    try {
      await apiMain.sendGifMetrics({
        event: "playback",
        username: await this.getUserName(),
        duration: props.duration, // in ms
        gif: props.gif,
        source: props.context,
      });
    } catch (err) {
      console.error("Error sending metrics.");
    }
  }

  async getUserName(): Promise<string|null> {
    if (!(await isAuthenticated())) {
      return null;
    }

    const username = apiMain.getUserName();
    return username;
  }

  async getUserVerifiedStatus(): Promise<boolean|null> {
    if (!(await isAuthenticated())) {
      return null;
    }

    const isVerified = apiMain.getUserVerifiedStatus();
    return isVerified;
  }
}

export const metrics = new Metrics();
